import { AnalyticsStore, PageStore, RouteStore } from 'bernie-core';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';
import { inject } from 'mobx-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { UitkSecondaryButton } from 'uitk-react-button';
import { UitkLoader } from 'uitk-react-loader';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading } from 'uitk-react-text';
import { ActivityDestinationViewSearch, ActivityMessageType } from '../../../../common/__generated__/api';
import { getPageId, getPageSize, getSeeAllURL } from '../../../../helpers/common-helper';
import { onClickScrollToTarget } from '../../../../helpers/smooth-scroll';
import { ActivitySearchStore, SearchCriteriaStore } from '../../../../stores';
import { trackAction } from '../../../common/banners/activity-message-banner-helper';
import { GridLayoutComponent } from './grid-layout-component';
import { ListLayoutComponent } from './list-layout-component';

interface DestinationActivityListProps extends LocalizedComponentProps {
  activityGroup: ActivityDestinationViewSearch.ActivityGroups;
  activityTiles: ActivityDestinationViewSearch.ActivityTiles[];
  regionName?: string;
  regionId?: string;
  routeStore?: RouteStore;
  analytics?: AnalyticsStore;
  activitySearchStore: ActivitySearchStore;
  searchCriteriaStore: SearchCriteriaStore;
  page: PageStore;
  showMoreLoading?: boolean;
  index: number;
}

@withLocalization
@inject('analytics', 'page', 'routeStore')
export class DestinationActivityList extends React.Component<DestinationActivityListProps> {
  public focusList: React.RefObject<any> | null;

  public constructor(props: DestinationActivityListProps) {
    super(props);
    this.focusList = React.createRef();
  }

  public componentDidMount(): void | null {
    const { activityTiles } = this.props;

    const sourcedCampaignMsg: ActivityDestinationViewSearch.ActivityTiles | null | undefined = activityTiles.find(
      (tile: ActivityDestinationViewSearch.ActivityMessageTileInlineFragment) =>
        tile.type === ActivityMessageType.SOURCED_CAMPAIGN
    ) as ActivityDestinationViewSearch.ActivityMessageTileInlineFragment;

    if (sourcedCampaignMsg?.analytics?.referrerId && this.props.analytics) {
      trackAction(
        sourcedCampaignMsg.analytics.referrerId,
        sourcedCampaignMsg.analytics.linkName,
        this.props.analytics,
        []
      );
    }
  }

  public componentDidUpdate(): void {
    const { searchCriteriaStore } = this.props;

    if (this.focusList?.current && searchCriteriaStore.activityToFocus.index !== null) {
      searchCriteriaStore.activityToFocus.element = this.focusList.current.querySelectorAll(
        'a[data-testid="card-link"]'
      )[searchCriteriaStore.activityToFocus.index];
    }
  }

  private handleSrpTileClick = (
    activityTile: ActivityDestinationViewSearch.ActivityInlineFragment,
    index: number
  ): void | null => {
    const { analytics, searchCriteriaStore } = this.props;
    if (!analytics) {
      return null;
    }
    searchCriteriaStore.scrollPosition = window.pageYOffset;

    searchCriteriaStore.activityToFocus.index = index;

    if (activityTile.analytics) {
      analytics.trackEvent(activityTile.analytics.referrerId, activityTile.analytics.linkName, analytics.omnitureData);
    }
  };

  private handleShowAllClick = (action: any): void | null => {
    const { analytics } = this.props;
    if (!analytics) {
      return null;
    }
    onClickScrollToTarget({ scrollTo: 0 });

    analytics.trackEvent(action.analytics.referrerId, action.analytics.linkName, analytics.omnitureData);
  };

  private getActivityTiles(
    activityTiles: ActivityDestinationViewSearch.ActivityTiles[],
    activitySearchStore: ActivitySearchStore
  ): JSX.Element | null {
    const cardLayoutComponentProps = {
      activityTiles: activityTiles,
      loading: false,
      onClick: this.handleSrpTileClick
    };

    return activitySearchStore.isGridLayoutListViewEnabled ? (
      <GridLayoutComponent {...cardLayoutComponentProps} />
    ) : (
      <ListLayoutComponent {...cardLayoutComponentProps} />
    );
  }

  private getSeeAllButton(
    seeAllUrl: string,
    activityGroupAction: ActivityDestinationViewSearch.__Action,
    classNamesArg: string
  ): JSX.Element | null {
    return (
      <Link
        data-testid="destination-activity-list-see-all"
        to={seeAllUrl}
        onClick={this.handleShowAllClick.bind(null, activityGroupAction)}
        type="button"
        className={classNamesArg}
      >
        <span className="uitk-button-container">
          <span className="uitk-button-text">{activityGroupAction?.label}</span>
        </span>
      </Link>
    );
  }

  private showMore = (): void | null => {
    const { analytics, page, searchCriteriaStore, activitySearchStore, index } = this.props;
    const pageId = getPageId(activitySearchStore);
    const pageSize = getPageSize(activitySearchStore);
    if (!analytics) {
      return null;
    }

    analytics.trackEvent(`LX.${pageId}.ShowMore`, 'show-more', analytics.omnitureData);
    searchCriteriaStore.showMore();
    searchCriteriaStore.scrollPosition = window.pageYOffset;

    const pageIndex = searchCriteriaStore.getPaginationIndex;
    searchCriteriaStore.activityToFocus.index = pageIndex * pageSize - 1;

    activitySearchStore.showMoreLoading = true;
    activitySearchStore.fetchActivitiesSearchDestinationView(
      searchCriteriaStore,
      analytics,
      {},
      searchCriteriaStore.searchVariables,
      page.pageId,
      index,
      false,
      false
    );
  };

  public render(): JSX.Element {
    const { l10n, activitySearchStore, activityGroup, index, activityTiles } = this.props;
    const seeAllUrl =
      this.props.routeStore && activityGroup.action
        ? getSeeAllURL(activityGroup.action.url, this.props.routeStore.queryParams)
        : '';
    const showSeeAllButton = activityGroup.action ? activityGroup.action.url !== 'Show More' : false;
    const displayShowMoreButton =
      activitySearchStore.showMoreButton && activityGroup.action ? activityGroup.action.url === 'Show More' : false;

    const showMoreLoading = activitySearchStore.showMoreLoading;

    return (
      <>
        <section
          id={`activity-list-${index}`}
          className="all-t-margin-six activity-list"
          data-testid="destination-activity-list--section"
        >
          <div className="all-x-margin-three" ref={this.focusList}>
            {activityGroup.name && (
              <UitkSpacing
                margin={{ blockend: 'two' }}
                padding={{ small: { blockstart: 'three' }, medium: { blockstart: 'four' } }}
              >
                <UitkHeading tag="h2" size={4}>
                  {activityGroup.name}
                </UitkHeading>
              </UitkSpacing>
            )}
            {activityTiles && this.getActivityTiles(activityTiles, activitySearchStore)}
            {activityGroup.action && showSeeAllButton && (
              <div className="all-t-padding-two uitk-link-align-center">
                <Viewport>
                  <ViewLarge>
                    <>
                      {this.getSeeAllButton(
                        seeAllUrl,
                        activityGroup.action,
                        'uitk-button uitk-button-small uitk-button-secondary'
                      )}
                    </>
                  </ViewLarge>
                  <ViewSmall>
                    <>
                      {this.getSeeAllButton(
                        seeAllUrl,
                        activityGroup.action,
                        'uitk-button uitk-button-small uitk-button-secondary uitk-button-fullWidth'
                      )}
                    </>
                  </ViewSmall>
                </Viewport>
              </div>
            )}
          </div>
        </section>

        {displayShowMoreButton && (
          <div className="uitk-type-center all-t-margin-two">
            {showMoreLoading && <UitkLoader className="view-all" />}
            <UitkSecondaryButton
              data-testid="destination-activity-list-show-more"
              disabled={showMoreLoading}
              onClick={this.showMore}
              text={l10n.formatText('lx.search.activityList.showMoreButton')}
            />
          </div>
        )}
      </>
    );
  }
}
